import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Added useNavigate for redirect
import { useSession } from '@supabase/auth-helpers-react'; // Added for session check
import api from '../services/api';

interface Job {
  id: string;
  title: string;
  description: string;
  // Add other fields as needed
}

type ApplicationDocumentType = 'cv' | 'payslip' | 'transcript' | 'workAuth';

interface JobApplication {
  id: string;
  appliedAt: string;
  screeningStatus: 'PENDING' | 'PASS' | 'FAIL' | 'NEEDS_REVIEW';
  documents: Record<ApplicationDocumentType, boolean>;
}

const documentLabels: Record<ApplicationDocumentType, string> = {
  cv: 'CV',
  payslip: 'Payslip',
  transcript: 'Academic transcript',
  workAuth: 'Work authorisation',
};

const documentTypes: ApplicationDocumentType[] = ['cv', 'payslip', 'transcript', 'workAuth'];

const EmployerJobs: React.FC = () => {
  const session = useSession(); // Check for valid session
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedJobId, setCopiedJobId] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [applicationsByJob, setApplicationsByJob] = useState<Record<string, JobApplication[]>>({});
  const [applicationsLoadingJobId, setApplicationsLoadingJobId] = useState<string | null>(null);
  const [documentLoadingKey, setDocumentLoadingKey] = useState<string | null>(null);
  const [documentErrorByKey, setDocumentErrorByKey] = useState<Record<string, string>>({});

  const getJobsErrorMessage = (error: any) => {
    const backendMessage = error.response?.data?.error || error.response?.data?.message;
    if (typeof backendMessage === 'string' && backendMessage.toLowerCase().includes('company id')) {
      return 'Your admin account is missing company metadata, so AI interview jobs cannot be saved yet.';
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'Your session is not authorized for AI Interviews. Log in as Jason to continue.';
    }
    return backendMessage || 'Failed to load jobs. Please refresh.';
  };

  useEffect(() => {
    if (!session) {
      navigate('/login?type=employer'); // Redirect if no session
      return;
    }

    const fetchJobs = async () => {
      try {
        const response = await api.get('/jobs');
        setJobs(response.data);
      } catch (error: any) {
        console.error('Error fetching jobs:', error);
        setError(getJobsErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [session, navigate]); // Depend on session/navigate

  const buildApplicationPath = (jobId: string) => `/jobs/${jobId}/apply`;

  const getDocumentKey = (applicationId: string, documentType: ApplicationDocumentType) => `${applicationId}:${documentType}`;

  const getApplicationsErrorMessage = (error: any) => {
    const backendMessage = error.response?.data?.error || error.response?.data?.message;
    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'Your session is not authorized to view AI interview applications. Log in as Jason to continue.';
    }
    return backendMessage || 'Unable to load applications for this job.';
  };

  const copyApplicationLink = async (jobId: string) => {
    const path = buildApplicationPath(jobId);
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedJobId(jobId);
      window.setTimeout(() => setCopiedJobId((current) => (current === jobId ? null : current)), 2500);
    } catch (copyError) {
      console.error('Unable to copy private application link:', copyError);
      setError('Unable to copy the private application link. Open the form and copy the browser URL instead.');
    }
  };

  const toggleApplications = async (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      return;
    }

    setExpandedJobId(jobId);
    if (applicationsByJob[jobId]) return;

    setApplicationsLoadingJobId(jobId);
    setError(null);
    try {
      const response = await api.get(`/applications?jobId=${jobId}`);
      setApplicationsByJob((current) => ({
        ...current,
        [jobId]: Array.isArray(response.data.applications) ? response.data.applications : [],
      }));
    } catch (error: any) {
      console.error('Error fetching applications:', error);
      setError(getApplicationsErrorMessage(error));
    } finally {
      setApplicationsLoadingJobId(null);
    }
  };

  const openApplicationDocument = async (applicationId: string, documentType: ApplicationDocumentType) => {
    const documentKey = getDocumentKey(applicationId, documentType);
    setDocumentLoadingKey(documentKey);
    setDocumentErrorByKey((current) => {
      const next = { ...current };
      delete next[documentKey];
      return next;
    });

    try {
      const response = await api.get(`/applications/${applicationId}/documents/${documentType}/signed-url`);
      const signedUrl = response.data?.signedUrl;
      if (typeof signedUrl !== 'string' || signedUrl.length === 0) {
        throw new Error('Document link was not returned.');
      }

      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      console.error('Unable to open application document:', error.response?.data || error.message);
      const backendMessage = error.response?.data?.error || error.response?.data?.message;
      setDocumentErrorByKey((current) => ({
        ...current,
        [documentKey]: backendMessage || 'Could not open this document. Try again to request a fresh link.',
      }));
    } finally {
      setDocumentLoadingKey(null);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="min-h-screen bg-surface-page text-txt-primary p-8">
      <h1 className="text-3xl font-bold mb-6">Manage Jobs</h1>
      <div className="mb-5 rounded-input border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">Private preview</p>
        <p>Application links are for Jason testing only. Candidate Applications are not public or launched.</p>
      </div>
      <Link to="/employer/create-job" className="bg-brand-600 hover:bg-brand-500 text-txt-primary px-6 py-2 rounded-input font-medium mb-4 inline-block">
        Create New Job
      </Link>
      {jobs.length > 0 ? (
        <table className="w-full border-collapse bg-surface-card rounded-input overflow-hidden">
          <thead>
            <tr>
              <th className="p-4 text-left border-b border-line">Title</th>
              <th className="p-4 text-left border-b border-line">Description</th>
              <th className="p-4 text-left border-b border-line">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => {
              const applications = applicationsByJob[job.id] || [];
              const isExpanded = expandedJobId === job.id;

              return (
                <React.Fragment key={job.id}>
                  <tr className="hover:bg-gray-700">
                    <td className="p-4 border-b border-line">{job.title}</td>
                    <td className="p-4 border-b border-line">{job.description}</td>
                    <td className="p-4 border-b border-line">
                      <div className="flex flex-col gap-2">
                        <Link to={`/employer/edit-job/${job.id}`} className="text-brand-600 hover:underline">Edit</Link> {/* Updated path to match App.tsx */}
                        <Link to={buildApplicationPath(job.id)} className="text-brand-600 hover:underline">
                          Open application form
                        </Link>
                        <button
                          type="button"
                          onClick={() => copyApplicationLink(job.id)}
                          className="self-start text-left text-brand-600 hover:underline"
                        >
                          {copiedJobId === job.id ? 'Private link copied' : 'Copy private application link'}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleApplications(job.id)}
                          className="self-start text-left text-brand-600 hover:underline"
                        >
                          {isExpanded ? 'Hide applications' : 'View applications'}
                        </button>
                        <span className="text-xs text-txt-muted">For Jason testing only</span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={3} className="border-b border-line bg-surface-page p-4">
                        <div className="rounded-input border border-line bg-surface-card p-4">
                          <h2 className="text-lg font-semibold">Applications and documents</h2>
                          <p className="mt-1 text-sm text-txt-secondary">
                            Document links are private and short-lived. A fresh link is requested only when you open a document.
                          </p>
                          {applicationsLoadingJobId === job.id ? (
                            <p className="mt-4 text-sm text-txt-secondary">Loading applications...</p>
                          ) : applications.length === 0 ? (
                            <p className="mt-4 text-sm text-txt-secondary">No applications have been submitted for this job yet.</p>
                          ) : (
                            <div className="mt-4 space-y-4">
                              {applications.map((application) => (
                                <div key={application.id} className="rounded-input border border-line bg-surface-page p-4">
                                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                      <p className="font-semibold">Application {application.id.slice(0, 8)}</p>
                                      <p className="text-sm text-txt-secondary">Screening status: {application.screeningStatus}</p>
                                    </div>
                                    <p className="text-xs text-txt-muted">
                                      Submitted {new Date(application.appliedAt).toLocaleString()}
                                    </p>
                                  </div>
                                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                                    {documentTypes.map((documentType) => {
                                      const documentKey = getDocumentKey(application.id, documentType);
                                      const hasDocument = Boolean(application.documents?.[documentType]);
                                      return (
                                        <div key={documentType} className="rounded-input border border-line bg-white p-3">
                                          <div className="flex items-center justify-between gap-3">
                                            <div>
                                              <p className="font-medium text-txt-primary">{documentLabels[documentType]}</p>
                                              <p className="text-xs text-txt-muted">
                                                {hasDocument ? 'Uploaded' : 'Not uploaded'}
                                              </p>
                                            </div>
                                            <button
                                              type="button"
                                              disabled={!hasDocument || documentLoadingKey === documentKey}
                                              onClick={() => openApplicationDocument(application.id, documentType)}
                                              className="rounded-input border border-brand-600 px-3 py-2 text-sm font-medium text-brand-600 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                                            >
                                              {documentLoadingKey === documentKey ? 'Opening...' : 'View document'}
                                            </button>
                                          </div>
                                          {documentErrorByKey[documentKey] && (
                                            <p className="mt-2 text-sm text-red-500">{documentErrorByKey[documentKey]}</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>No jobs found. Create one to get started.</p>
      )}
    </div>
  );
};

export default EmployerJobs;
